import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useChatContext } from "../../contexts";
import { Card } from "../ui/card";
import { cn } from "../../lib/utils";
import ChatInterface from "./ChatInterface";
import FloatingChatButton from "./FloatingChatButton";

// Custom hook for media queries
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addListener(listener);
    return () => media.removeListener(listener);
  }, [matches, query]);

  return matches;
};

/**
 * ChatWidget Component
 * Main wrapper that handles embedded vs floating modes
 */
const ChatWidget = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isSmallMobile = useMediaQuery("(max-width: 640px)");

  // Check if on homepage
  const isHomepage = location.pathname === "/";

  // Use chat context
  const {
    messages,
    sendMessage: sendChatMessage,
    isTyping,
    isLoading,
    isAvailable,
    error,
    isOpen,
    openChat,
    closeChat,
  } = useChatContext();

  const [inputValue, setInputValue] = useState("");

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const messageText = inputValue;
    setInputValue("");

    await sendChatMessage(messageText);
  };

  const handleSuggestionClick = async (suggestion) => {
    // Handle special action suggestions
    if (suggestion === "Try the Calculator") {
      setTimeout(() => {
        if (!embedded) {
          closeChat();
        }
        navigate("/search");
      }, 500);
      return;
    }

    // Handle regular questions - send directly without setting input
    await sendChatMessage(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Embedded mode - Always visible chat interface
  if (embedded) {
    return (
      <Card className="w-full h-[calc(100vh-150px)] sm:h-[550px] md:h-[600px] min-h-[600px] sm:min-h-[550px] md:min-h-[600px] flex flex-col rounded-sm sm:rounded-lg overflow-hidden border max-h-[calc(100vh-100px)] sm:max-h-[600px]">
        <ChatInterface
          messages={messages}
          isTyping={isTyping}
          isLoading={isLoading}
          isAvailable={isAvailable}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onSendMessage={handleSendMessage}
          onKeyPress={handleKeyPress}
          onSuggestionClick={handleSuggestionClick}
          isMobile={isMobile}
          isSmallMobile={isSmallMobile}
          placeholder={
            isMobile ? "Ask about Koflip..." : "Ask me about Koflip..."
          }
        />
      </Card>
    );
  }

  // Floating mode - Button + Popup window
  return (
    <>
      {/* Floating Chat Button */}
      <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-[1000]">
        {!isOpen && (
          <FloatingChatButton
            onClick={openChat}
            isAvailable={isAvailable}
          />
        )}
      </div>

      {/* Chat Popup Window */}
      <div
        className={cn(
          "fixed z-[1001] flex flex-col overflow-hidden transition-all duration-300",
          "bottom-0 sm:bottom-6 right-0 sm:right-6 left-0 sm:left-auto",
          "w-full sm:w-[400px]",
          "rounded-none sm:rounded-lg shadow-2xl",
          isOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-full pointer-events-none"
        )}
        style={
          isMobile
            ? {
                height: "100svh", // Small viewport height for mobile
                maxHeight: "100vh", // Fallback
              }
            : {
                height: "600px",
                maxHeight: "80vh",
              }
        }
      >
        <Card className="w-full h-full flex flex-col bg-background border-0 sm:border">
          <ChatInterface
            messages={messages}
            isTyping={isTyping}
            isLoading={isLoading}
            isAvailable={isAvailable}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onSendMessage={handleSendMessage}
            onKeyPress={handleKeyPress}
            onSuggestionClick={handleSuggestionClick}
            onClose={closeChat}
            showCloseButton={true}
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
            placeholder="Ask me about Koflip..."
          />
        </Card>
      </div>
    </>
  );
};

export default ChatWidget;
